import { useState } from "react";
import { MAX_ENRICH_ITEMS, thumbPath } from "@iltocat/shared";
import { plural } from "../lib/api.js";
import { useItems } from "../lib/useItems.js";
import { EmptyState } from "./EmptyState.js";
import { LoadMore } from "./LoadMore.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";
import { Segmented } from "@vcnet/ui";

/**
 * Step one of enrichment when nobody arrived with an item in hand: tick the
 * things to describe.
 *
 * It starts on "Needs details" because that is the pile a fast capture session
 * leaves, and the pile this stage exists to empty. The cap is the prompt's, not
 * this screen's: more than `MAX_ENRICH_ITEMS` is a prompt nobody wants to paste
 * and a reply a model tends to cut short.
 */
export function EnrichPicker({ onChoose }: { onChoose: (ids: string[]) => void }) {
  const [pile, setPile] = useState<"needs" | "all">("needs");
  const [chosen, setChosen] = useState<string[]>([]);
  const list = useItems(pile === "needs" ? { needsDetails: true } : {});

  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id)
        ? current.filter((existing) => existing !== id)
        : current.length < MAX_ENRICH_ITEMS
          ? [...current, id]
          : current,
    );

  return (
    <>
      <div className="toolbar-row">
        <Segmented
          label="Show"
          value={pile}
          onChange={setPile}
          options={[
            { value: "needs", label: "Needs details" },
            { value: "all", label: "Everything" },
          ]}
        />
      </div>

      {list.error && <Notice tone="error">{list.error}</Notice>}

      {list.loading ? (
        <ul className="grid" aria-busy="true" aria-label="Loading">
          {Array.from({ length: 6 }, (_, index) => (
            <li key={index} className="card card-skeleton" />
          ))}
        </ul>
      ) : list.items.length === 0 ? (
        pile === "needs" ? (
          <EmptyState icon="check-circle" title="All caught up">
            Every item has a name. Switch to Everything to pick any of them.
          </EmptyState>
        ) : (
          <EmptyState icon="box" title="Nothing here yet" />
        )
      ) : (
        <ul className="grid">
          {list.items.map((item) => {
            const on = chosen.includes(item.id);
            return (
              <li key={item.id}>
                <label className={on ? "card pick pick-on" : "card pick"}>
                  <input
                    className="pick-check"
                    type="checkbox"
                    checked={on}
                    disabled={!on && chosen.length >= MAX_ENRICH_ITEMS}
                    onChange={() => toggle(item.id)}
                  />
                  <span className="card-shot">
                    {item.coverPhotoId ? (
                      <img src={thumbPath(item.coverPhotoId)} alt="" loading="lazy" />
                    ) : (
                      <span className="card-noshot" aria-hidden="true">
                        <Icon name="image" size={28} />
                      </span>
                    )}
                    <span className="pick-mark" aria-hidden="true">
                      <Icon name="check" size={16} />
                    </span>
                  </span>
                  <span className="card-body">
                    <span className={item.needsDetails ? "card-title card-untitled" : "card-title"}>
                      {item.title || "Needs details"}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <LoadMore more={list.more} loading={list.loadingMore} onMore={list.showMore} />

      <div className="action-bar">
        <p className="action-bar-status">
          {chosen.length === 0
            ? `Pick up to ${MAX_ENRICH_ITEMS}.`
            : `${chosen.length} of ${MAX_ENRICH_ITEMS} chosen`}
        </p>
        <button
          type="button"
          className="button"
          disabled={chosen.length === 0}
          onClick={() => onChoose(chosen)}
        >
          <Icon name="sparkles" size={18} />
          {chosen.length === 0 ? "Build prompt" : `Build prompt for ${plural(chosen.length, "item")}`}
        </button>
      </div>
    </>
  );
}
